import { Component, ElementRef, Input, OnChanges, OnDestroy, ViewChild } from '@angular/core';
import { formatDate } from '@angular/common';
import { finalize, Subject, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { EExamKey } from '@gray/domain/enums/exam-key.enum';
import { CoachStudentService } from '../../../core/services/coach-student.service';
import { CoachPeriodService } from '../../../core/services/coach-period.service';
import { IStudentExamHistory, IStudentExamScore } from '../../../core/models/student-overview.model';

/** How many exams the chart asks for; the API caps it anyway. */
const EXAM_LIMIT = 6;
const CHART_HEIGHT = 190;

/**
 * The student's net over their last exams, for the exam type the detail header is on.
 *
 * The chart is drawn with ApexCharts, which the workspace already depends on and the student app
 * loads the same way — lazily, so it stays out of the initial bundle.
 */
@Component({
  selector: 'coach-exam-history-card',
  templateUrl: './exam-history-card.component.html',
  styleUrl: './exam-history-card.component.scss',
})
export class ExamHistoryCardComponent implements OnChanges, OnDestroy {
  @Input() studentId!: number;
  @Input() examKey!: EExamKey;
  @Input() examName = '';

  @ViewChild('chartHost') chartHost?: ElementRef<HTMLElement>;

  history?: IStudentExamHistory;
  isLoading = false;

  destroyed$ = new Subject();

  private chart?: { render: () => void; destroy: () => void };

  constructor(
    private studentService: CoachStudentService,
    private periodService: CoachPeriodService,
    private translate: TranslateService,
  ) {
  }

  ngOnChanges(): void {
    if (this.studentId && this.examKey) {
      this.getScores();
    }
  }

  scores(): IStudentExamScore[] {
    return this.history?.scores ?? [];
  }

  lastScore(): number | null {
    return this.scores().length ? this.scores()[this.scores().length - 1].score : null;
  }

  /** Difference against the exam before it — the pill next to the header. */
  delta(): number | null {
    if (this.scores().length < 2) {
      return null;
    }

    const last = this.scores()[this.scores().length - 1].score;
    const previous = this.scores()[this.scores().length - 2].score;
    return Number((last - previous).toFixed(2));
  }

  /** No period is sent: the chart follows the student's whole run of exams, not one period. */
  getScores() {
    this.isLoading = true;
    this.studentService.getStudentExamScores(this.studentId, `?examKey=${this.examKey}&limit=${EXAM_LIMIT}`)
      .pipe(takeUntil(this.destroyed$), finalize(() => this.isLoading = false))
      .subscribe(r => {
        this.history = r.data as IStudentExamHistory;
        // The host only exists once the loading state is gone.
        setTimeout(() => this.renderChart());
      });
  }

  private async renderChart() {
    this.chart?.destroy();
    this.chart = undefined;

    const host = this.chartHost?.nativeElement;
    if (!host || this.scores().length < 2) {
      return;
    }

    const module = await import('apexcharts');
    const ApexCharts = module.default;

    this.chart = new ApexCharts(host, this.buildOptions());
    this.chart.render();
  }

  private buildOptions() {
    const accent = this.cssValue('--brand-primary', '#FFD200');
    const grid = this.cssValue('--gray-100', '#E7E7E7');
    const muted = this.cssValue('--gray-400', '#888888');
    const goal = this.history?.goal ?? null;

    return {
      chart: {
        type: 'area',
        height: CHART_HEIGHT,
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily: 'inherit',
        parentHeightOffset: 0,
      },
      series: [{
        name: this.translate.instant('students.exam_history.net'),
        data: this.scores().map(score => score.score),
      }],
      colors: [accent],
      stroke: { curve: 'smooth', width: 3 },
      fill: {
        type: 'gradient',
        gradient: { shadeIntensity: 1, opacityFrom: .35, opacityTo: 0, stops: [0, 100] },
      },
      dataLabels: { enabled: false },
      grid: {
        borderColor: grid,
        strokeDashArray: 4,
        xaxis: { lines: { show: false } },
        padding: { left: 4, right: 4 },
      },
      markers: { size: 0, hover: { size: 6 } },
      xaxis: {
        categories: this.scores().map(score => this.dateLabel(score)),
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { style: { colors: muted, fontSize: '11px', fontWeight: 600 } },
        tooltip: { enabled: false },
      },
      yaxis: {
        min: 0,
        max: this.chartMax(goal),
        tickAmount: 4,
        labels: {
          style: { colors: muted, fontSize: '11px' },
          formatter: (value: number) => `${Math.round(value)}`,
        },
      },
      // The publisher belongs in the tooltip: the axis carries the date.
      tooltip: {
        custom: ({ dataPointIndex }: { dataPointIndex: number }) => this.tooltipHtml(dataPointIndex),
      },
      annotations: goal === null ? {} : {
        yaxis: [{
          y: goal,
          strokeDashArray: 6,
          borderColor: muted,
          label: {
            text: this.translate.instant('students.exam_history.goal', { score: goal }),
            position: 'right',
            textAnchor: 'end',
            style: { background: 'transparent', color: muted, fontSize: '11px', fontWeight: 700 },
          },
        }],
      },
    };
  }

  /** Headroom above the highest of the scores and the goal, rounded to a friendly number. */
  private chartMax(goal: number | null): number {
    const highest = Math.max(...this.scores().map(score => score.score), goal ?? 0);
    return Math.max(10, Math.ceil((highest * 1.15) / 10) * 10);
  }

  private tooltipHtml(index: number): string {
    const score = this.scores()[index];
    if (!score) {
      return '';
    }

    const publisher = score.publisher?.name ?? this.translate.instant('students.exam_history.unknown_publisher');
    const net = this.translate.instant('students.exam_history.net');

    return `
      <div class="exam-tooltip">
        <p class="exam-tooltip-title">${publisher}</p>
        <p class="exam-tooltip-date">${this.dateLabel(score)}</p>
        <p class="exam-tooltip-value">${score.score.toLocaleString('tr-TR')} ${net}</p>
      </div>
    `;
  }

  private dateLabel(score: IStudentExamScore): string {
    return score.examDate ? formatDate(score.examDate, 'd MMM', 'tr-TR') : '—';
  }

  private cssValue(name: string, fallback: string): string {
    const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return value || fallback;
  }

  ngOnDestroy(): void {
    this.chart?.destroy();
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
